import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { podcastsApi } from "../api/podcastsApi";
import { useAuth } from "../auth/authStore";
import { useToast } from "../ui/toast/ToastProvider";
import { usePodcastPlayer } from "../podcasts/playerStore";
import { isAegisEmbedUrl, resolveAegisPlayerUrl } from "../podcasts/aegisPlayer";
import { useWs } from "../ws/wsStore";
import { getSiteOrigin, joinUrl } from "../utils/seo";
import { buildPsychologistProfilePath } from "../utils/psychologistProfile";
import { REACTION_OPTIONS } from "../ui/reactions/reactionKinds";
import { buildNextReactionState, buildReactionButtons as buildUnifiedReactionButtons, normalizeReactionKindForApi } from "../ui/reactions/reactionModel";
import ReportContentButton from "../components/reports/ReportContentButton";

const REACTION_PRESETS = REACTION_OPTIONS.map((item) => ({
  type: item.kind,
  kind: item.kind,
  emoji: item.emoji,
  label: item.label,
}));

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function safeText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function initials(name) {
  const parsed = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0])
    .join("");
  return parsed ? parsed.toUpperCase() : "BS";
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeText(value, "—");
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDuration(seconds) {
  const sec = Number(seconds);
  if (!Number.isFinite(sec) || sec <= 0) return "—";
  const mins = Math.floor(sec / 60);
  const rest = sec % 60;
  if (rest === 0) return `${mins} мин`;
  return `${mins}:${String(rest).padStart(2, "0")}`;
}

function formatClock(seconds) {
  const sec = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(sec / 60);
  const rest = sec % 60;
  return `${String(mins).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

function normalizeType(value) {
  return normalizeReactionKindForApi(value, "");
}

function parsePositiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parsePodcastEpisodeIdFromUrl(rawUrl = "") {
  const value = String(rawUrl || "").trim();
  if (!value) return null;
  const match = value.match(/\/podcasts\/episodes\/([^?#/]+)/i);
  if (!match?.[1]) return null;
  const token = String(match[1]).trim();
  const numeric = token.match(/^(\d+)/);
  return parsePositiveId(numeric ? numeric[1] : token);
}

function extractPodcastRealtimeMeta(payload) {
  if (!payload || typeof payload !== "object") return null;

  const dto =
    payload.notification && typeof payload.notification === "object"
      ? payload.notification
      : payload;
  const dtoPayload = dto.payload && typeof dto.payload === "object" ? dto.payload : {};
  const rawType = String(
    dtoPayload.action ||
      payload.action ||
      dto.action ||
      payload.eventType ||
      dto.eventType ||
      payload.type ||
      dto.type ||
      ""
  )
    .trim()
    .toUpperCase();
  const episodeId = parsePositiveId(
    dtoPayload.episodeId ??
      dto.episodeId ??
      dtoPayload.podcastEpisodeId ??
      dto.podcastEpisodeId ??
      dtoPayload.episode?.id ??
      dto.episode?.id ??
      parsePodcastEpisodeIdFromUrl(
        dtoPayload.linkUrl || dto.linkUrl || payload.linkUrl || ""
      )
  );
  const commentId = parsePositiveId(
    dtoPayload.commentId ?? dto.commentId ?? dtoPayload.comment?.id ?? dto.comment?.id
  );

  const isPodcastEvent =
    rawType.includes("PODCAST") ||
    rawType.includes("EPISODE_COMMENT") ||
    rawType.includes("EPISODE_REACTION");
  if (!isPodcastEvent && !episodeId && !commentId) return null;

  return {
    type: rawType || "PODCAST_UPDATED",
    episodeId,
    commentId,
  };
}

function parseTimeToSeconds(value) {
  if (Number.isFinite(Number(value))) return Math.max(0, Number(value));
  const text = String(value || "").trim();
  if (!text.includes(":")) return 0;
  const chunks = text.split(":").map((chunk) => Number(chunk));
  if (chunks.some((chunk) => !Number.isFinite(chunk))) return 0;
  if (chunks.length === 2) return chunks[0] * 60 + chunks[1];
  if (chunks.length === 3) return chunks[0] * 3600 + chunks[1] * 60 + chunks[2];
  return 0;
}

function normalizeTimecode(item = {}, index = 0) {
  const raw = asObject(item);
  const seconds = parseTimeToSeconds(raw.seconds ?? raw.timeSec ?? raw.timeSeconds ?? raw.timestamp ?? raw.time);
  const label =
    safeText(raw.label || raw.title || raw.text || raw.name) ||
    `Момент ${index + 1}`;

  return {
    id: raw.id ?? `${seconds}-${index}`,
    seconds,
    stamp: formatClock(seconds),
    label,
  };
}

function normalizeEpisode(input = {}) {
  const raw = asObject(input);
  const author = asObject(raw.author || raw.psychologist || raw.owner);

  const aegisPlayerUrl = resolveAegisPlayerUrl(raw);
  const playbackUrl = safeText(raw.playbackUrl || raw.audioUrl || raw.streamUrl || raw.mediaUrl);
  const externalUrl = safeText(raw.externalUrl);
  const resolvedAegisPlayerUrl =
    aegisPlayerUrl ||
    (isAegisEmbedUrl(playbackUrl)
      ? playbackUrl
      : isAegisEmbedUrl(externalUrl)
        ? externalUrl
        : "");

  return {
    id: raw.id ?? raw.episodeId ?? null,
    podcastId: raw.podcastId ?? null,
    podcastSlug: safeText(raw.podcastSlug || raw.slug),
    podcastTitle: safeText(raw.podcastTitle || raw.showTitle || "Подкаст"),
    title: safeText(raw.title || "Эпизод"),
    description: safeText(raw.description || raw.shortDescription || ""),
    coverImageUrl: safeText(
      raw.coverImageUrl || raw.imageUrl || raw.coverUrl || raw.podcastCoverUrl || raw.podcastCoverImageUrl
    ),
    playbackUrl,
    externalUrl,
    aegisAssetId: safeText(raw.aegisAssetId || raw.assetId || raw.aegis_asset_id || raw.asset_id),
    aegisProjectId: safeText(raw.aegisProjectId || raw.projectId || raw.aegis_project_id || raw.project_id),
    aegisPlayerUrl: resolvedAegisPlayerUrl,
    aegisEmbedUrl: resolvedAegisPlayerUrl,
    isAegisEmbed: Boolean(resolvedAegisPlayerUrl),
    publishedAt: raw.publishedAt || raw.publishDate || raw.createdAt || null,
    durationSeconds: toNumber(raw.durationSeconds || raw.lengthSeconds || raw.audioLengthSeconds, 0),
    listensCount: toNumber(raw.listensCount || raw.totalListens, 0),
    authorName: safeText(author.name || raw.authorName || "Специалист"),
    authorUserId: author.userId ?? author.id ?? raw.authorUserId ?? null,
    tags: asArray(raw.tags)
      .map((item) => safeText(typeof item === "string" ? item : item?.title || item?.name || item?.label))
      .filter(Boolean),
    timecodes: asArray(raw.timecodes || raw.moments || raw.chapters).map(normalizeTimecode),
    raw,
  };
}

function normalizeReactionState(episode = {}) {
  const source =
    episode?.raw?.reactionStats ||
    episode?.raw?.reactions ||
    episode?.raw?.reactionCounts ||
    episode?.raw?.stats?.reactions ||
    {};

  const counts = {};
  let activeType = normalizeType(
    episode?.raw?.myReactionKind ||
      episode?.raw?.myReactionType ||
      episode?.raw?.myReaction ||
      episode?.raw?.viewerReaction ||
      episode?.raw?.selectedReaction ||
      ""
  );

  if (Array.isArray(source)) {
    source.forEach((item) => {
      const data = asObject(item);
      const type = normalizeType(data.type || data.code || data.reactionType);
      if (!type) return;
      counts[type] = toNumber(data.count || data.total || data.value, 0);
      if (!activeType && Boolean(data.mine || data.active || data.selected || data.me)) {
        activeType = type;
      }
    });
  } else if (source && typeof source === "object") {
    Object.entries(source).forEach(([key, value]) => {
      const type = normalizeType(key);
      if (!type) return;
      counts[type] = toNumber(value, 0);
    });
  }

  // Fallback for toggle-like payloads that only return a single total count and active kind.
  if (!Object.keys(counts).length) {
    const total = Math.max(
      0,
      toNumber(
        episode?.raw?.reactionsTotal ??
          episode?.raw?.reactionsCount ??
          episode?.raw?.count ??
          episode?.raw?.total ??
          0,
        0
      )
    );
    if (total > 0 || activeType) {
      counts[activeType || "LIKE"] = total;
    }
  }

  return {
    counts,
    activeType,
  };
}

function buildReactionButtons(reactionState) {
  return buildUnifiedReactionButtons(reactionState, REACTION_PRESETS);
}

function normalizeComment(input = {}, inheritedParentId = null) {
  const raw = asObject(input);
  const author = asObject(raw.author || raw.user || raw.owner);
  const repliesRaw = asArray(raw.replies || raw.children || raw.items);

  return {
    id: raw.id ?? raw.commentId ?? raw.uuid ?? null,
    parentCommentId: raw.parentCommentId ?? raw.parentId ?? inheritedParentId ?? null,
    authorName: safeText(author.name || raw.authorName || raw.userName || "Пользователь"),
    authorUserId: author.userId ?? author.id ?? raw.authorUserId ?? raw.userId ?? null,
    authorAvatarUrl: safeText(author.avatarUrl || raw.authorAvatarUrl || raw.avatarUrl),
    content: safeText(raw.content || raw.text || raw.message || ""),
    createdAt: raw.createdAt || raw.updatedAt || raw.timestamp || null,
    createdLabel: formatDate(raw.createdAt || raw.updatedAt || raw.timestamp || null),
    likesCount: toNumber(raw.likesCount || raw.likeCount || raw.reactionsCount || 0),
    likedByMe: Boolean(raw.likedByMe || raw.isLikedByMe || raw.myReaction || raw.reacted),
    replies: repliesRaw.map((item) => normalizeComment(item, raw.id ?? raw.commentId ?? null)),
  };
}

function flattenComments(nodes = [], accumulator = []) {
  asArray(nodes).forEach((node) => {
    const item = normalizeComment(node, null);
    const { replies, ...self } = item;
    accumulator.push({ ...self, replies: [] });
    if (Array.isArray(replies) && replies.length) {
      replies.forEach((reply) => {
        flattenComments([{ ...reply, parentCommentId: reply.parentCommentId ?? self.id }], accumulator);
      });
    }
  });
  return accumulator;
}

function sortByCreatedDesc(items = []) {
  return [...items].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });
}

function buildCommentTree(rawPayload) {
  const source =
    asArray(rawPayload) ||
    asArray(rawPayload?.items) ||
    asArray(rawPayload?.content);

  const normalizedFlat = flattenComments(source);
  if (!normalizedFlat.length) return [];

  const map = new Map();
  normalizedFlat.forEach((item) => {
    if (item.id === null || item.id === undefined) return;
    map.set(item.id, { ...item, replies: [] });
  });

  const roots = [];
  normalizedFlat.forEach((item) => {
    const id = item.id;
    if (id === null || id === undefined) return;
    const current = map.get(id);
    if (!current) return;
    const parentId = item.parentCommentId;
    if (parentId !== null && parentId !== undefined && map.has(parentId)) {
      map.get(parentId).replies.push(current);
      return;
    }
    roots.push(current);
  });

  const walkSort = (items) =>
    sortByCreatedDesc(items).map((item) => ({
      ...item,
      replies: walkSort(item.replies || []),
    }));

  return walkSort(roots);
}

function countComments(items = []) {
  return asArray(items).reduce((sum, item) => sum + 1 + countComments(item?.replies || []), 0);
}

function mapComments(items = [], mapper) {
  return asArray(items).map((item) => {
    const next = mapper(item);
    if (next !== item) return next;
    const replies = mapComments(item.replies || [], mapper);
    if (replies !== item.replies) return { ...item, replies };
    return item;
  });
}

function extractEpisodesFromPodcastProfile(rawPayload) {
  const payload = asObject(rawPayload);
  const pageLike =
    payload.episodesPage ||
    payload.episodes ||
    payload.items ||
    asObject(payload.item).episodesPage ||
    asObject(payload.item).episodes ||
    [];

  const source = Array.isArray(pageLike)
    ? pageLike
    : asArray(pageLike.content || pageLike.items);

  return source.map((row) => {
    const normalized = normalizeEpisode(row);
    return {
      id: normalized.id,
      title: normalized.title,
      durationLabel: formatDuration(normalized.durationSeconds),
      coverImageUrl: normalized.coverImageUrl,
      podcastTitle: normalized.podcastTitle,
      publishedLabel: formatDate(normalized.publishedAt),
      playbackUrl: normalized.playbackUrl,
      aegisAssetId: normalized.aegisAssetId,
      aegisProjectId: normalized.aegisProjectId,
      isAegisEmbed: normalized.isAegisEmbed,
    };
  });
}

function extractSubscriptions(rawPayload) {
  const payload = asObject(rawPayload);
  const source = Array.isArray(payload)
    ? payload
    : asArray(payload.items || payload.content || payload.item?.items || payload.item?.content);

  return source.map((row) => {
    const raw = asObject(row);
    return {
      id: raw.id ?? raw.podcastId ?? raw.showId ?? null,
      title: safeText(raw.title || raw.name || raw.podcastTitle || "Подкаст"),
      coverImageUrl: safeText(raw.coverImageUrl || raw.imageUrl || raw.coverUrl),
      badge: safeText(raw.latestEpisodeTitle || raw.lastEpisodeTitle),
    };
  });
}

function extractQueryValue(search, key) {
  const params = new URLSearchParams(String(search || ""));
  return params.get(key) || "";
}

export default function PodcastEpisode() {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, me, role, logout } = useAuth();
  const player = usePodcastPlayer();
  const { connected: wsConnected, subscribe: wsSubscribe } = useWs();
  const realtimeRefreshTimerRef = React.useRef(null);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [episode, setEpisode] = React.useState(null);
  const [reactionState, setReactionState] = React.useState({ counts: {}, activeType: "" });
  const [comments, setComments] = React.useState([]);
  const [commentsLoading, setCommentsLoading] = React.useState(false);
  const [commentDraft, setCommentDraft] = React.useState("");
  const [replyDraftById, setReplyDraftById] = React.useState({});
  const [replyOpenById, setReplyOpenById] = React.useState({});
  const [relatedEpisodes, setRelatedEpisodes] = React.useState([]);
  const [subscriptions, setSubscriptions] = React.useState([]);

  const canManagePodcasts = ["PSYCHOLOGIST", "ADMIN", "MODERATOR"].includes(String(role || "").toUpperCase());

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(
    () => joinUrl(origin, `/podcasts/episodes/${encodeURIComponent(String(episodeId || ""))}`),
    [origin, episodeId]
  );
  const applyEpisodePayload = React.useCallback((raw) => {
    const normalized = normalizeEpisode(raw || {});
    setEpisode(normalized);
    setReactionState((prev) => {
      const next = normalizeReactionState(normalized);
      const nextHasData = Boolean(Object.keys(next.counts || {}).length || next.activeType);
      if (!nextHasData) return prev;
      if (!next.activeType && prev?.activeType) {
        return {
          counts: Object.keys(next.counts || {}).length ? next.counts : prev.counts,
          activeType: prev.activeType,
        };
      }
      return next;
    });
    return normalized;
  }, []);
  const openPsychologistProfile = React.useCallback(
    (event, userId) => {
      const path = buildPsychologistProfilePath(userId);
      if (!path) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      navigate(path);
    },
    [navigate]
  );

  const loadComments = React.useCallback(async () => {
    if (!episodeId) {
      setComments([]);
      return;
    }

    setCommentsLoading(true);
    try {
      const rawComments = await podcastsApi.getEpisodeComments(episodeId);
      setComments(buildCommentTree(rawComments));
    } catch (err) {
      if (Number(err?.status) === 404) {
        setComments([]);
      } else {
        toast.error(err?.message || "Не удалось загрузить комментарии");
      }
    } finally {
      setCommentsLoading(false);
    }
  }, [episodeId, toast]);

  const reloadEpisodeRealtime = React.useCallback(async () => {
    if (!episodeId) return;
    try {
      const raw = await podcastsApi.getEpisodeDetails(episodeId);
      applyEpisodePayload(raw);
    } catch {
      // ignore realtime refresh errors
    }
  }, [applyEpisodePayload, episodeId]);

  const scheduleRealtimeRefresh = React.useCallback(() => {
    if (realtimeRefreshTimerRef.current) {
      window.clearTimeout(realtimeRefreshTimerRef.current);
    }
    realtimeRefreshTimerRef.current = window.setTimeout(() => {
      realtimeRefreshTimerRef.current = null;
      void reloadEpisodeRealtime();
      void loadComments();
    }, 180);
  }, [loadComments, reloadEpisodeRealtime]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    podcastsApi
      .getEpisodeDetails(episodeId)
      .then((raw) => {
        if (cancelled) return;
        const normalized = applyEpisodePayload(raw);
        if (normalized?.id) {
          podcastsApi.registerListen(normalized.id)
            .then((listen) => {
              if (cancelled) return;
              const nextEpisodeListens = Number(listen?.episodeListens);
              if (!Number.isFinite(nextEpisodeListens)) return;
              setEpisode((prev) => (prev ? { ...prev, listensCount: nextEpisodeListens } : prev));
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Не удалось загрузить эпизод");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyEpisodePayload, episodeId]);

  React.useEffect(() => {
    loadComments();
  }, [loadComments]);

  React.useEffect(() => {
    return () => {
      if (realtimeRefreshTimerRef.current) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || !wsConnected) return undefined;

    const episodeNumericId = parsePositiveId(episode?.id ?? episodeId);
    const onRealtimePayload = (payload) => {
      const meta = extractPodcastRealtimeMeta(payload);
      if (!meta) return;
      if (!meta.episodeId) return;
      if (episodeNumericId && meta.episodeId !== episodeNumericId) return;
      scheduleRealtimeRefresh();
    };

    const unsubscribers = [wsSubscribe("/user/queue/notifications", onRealtimePayload)];
    if (episodeNumericId) {
      unsubscribers.push(
        wsSubscribe(`/topic/podcasts/episodes/${episodeNumericId}`, onRealtimePayload)
      );
      unsubscribers.push(
        wsSubscribe(`/topic/podcast/episodes/${episodeNumericId}`, onRealtimePayload)
      );
    }

    return () => {
      unsubscribers.forEach((unsub) => {
        try {
          unsub?.();
        } catch {}
      });
    };
  }, [
    episode?.id,
    episodeId,
    isAuthenticated,
    scheduleRealtimeRefresh,
    wsConnected,
    wsSubscribe,
  ]);

  React.useEffect(() => {
    if (!episode) return;
    const podcastRef = episode.podcastSlug || episode.podcastId;
    if (!podcastRef) return;

    podcastsApi
      .getPodcastProfile(podcastRef, { page: 0, size: 8 })
      .then((raw) => {
        const items = extractEpisodesFromPodcastProfile(raw || {}).filter((item) => String(item.id) !== String(episode.id));
        setRelatedEpisodes(items.slice(0, 5));
      })
      .catch(() => {
        setRelatedEpisodes([]);
      });
  }, [episode]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setSubscriptions([]);
      return;
    }

    podcastsApi
      .getMySubscriptions({ page: 0, size: 6 })
      .then((raw) => {
        setSubscriptions(extractSubscriptions(raw));
      })
      .catch(() => {
        setSubscriptions([]);
      });
  }, [isAuthenticated]);

  React.useEffect(() => {
    setSearchInput((prev) => {
      const fromQuery = extractQueryValue(window.location.search, "q");
      return prev || fromQuery;
    });
  }, []);

  const episodeInPlayer = String(player.track?.episodeId || "") === String(episode?.id || "");
  const usesAegisPlayer = Boolean(episode?.aegisPlayerUrl || episode?.isAegisEmbed);
  const currentDuration = episodeInPlayer ? player.duration || episode?.durationSeconds || 0 : episode?.durationSeconds || 0;
  const currentProgress = currentDuration > 0 ? Math.min(100, ((episodeInPlayer ? player.currentTime : 0) / currentDuration) * 100) : 0;

  const reactionButtons = React.useMemo(() => buildReactionButtons(reactionState), [reactionState]);
  const commentsCount = React.useMemo(() => countComments(comments), [comments]);

  const onSearchSubmit = (event) => {
    event.preventDefault();
    const nextQuery = safeText(searchInput);
    navigate(`/podcasts${nextQuery ? `?q=${encodeURIComponent(nextQuery)}` : ""}`);
  };

  const playCurrentEpisode = async (seekToValue = null) => {
    if (!episode) return;
    const ok = await player.playTrack(
      {
        id: episode.id,
        episodeId: episode.id,
        title: episode.title,
        podcastTitle: episode.podcastTitle,
        podcastSlug: episode.podcastSlug,
        podcastId: episode.podcastId,
        coverImageUrl: episode.coverImageUrl,
        durationSeconds: episode.durationSeconds,
        playbackUrl: episode.playbackUrl,
        assetId: episode.aegisAssetId,
        projectId: episode.aegisProjectId,
        isAegisEmbed: episode.isAegisEmbed,
      },
      {
        autoplay: true,
        seekTo: Number.isFinite(Number(seekToValue)) ? Number(seekToValue) : undefined,
      }
    );

    if (!ok && episode.externalUrl) {
      window.open(episode.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (!ok) {
      toast.info(player.error || "Не удалось запустить встроенный плеер для этого эпизода");
    }
  };

  const onSeekEpisodeProgress = async (event) => {
    const seconds = Number(event.target.value) || 0;
    if (!episodeInPlayer) {
      const ok = await player.playTrack(
        {
          id: episode?.id,
          episodeId: episode?.id,
          title: episode?.title,
          podcastTitle: episode?.podcastTitle,
          podcastSlug: episode?.podcastSlug,
          podcastId: episode?.podcastId,
          coverImageUrl: episode?.coverImageUrl,
          durationSeconds: episode?.durationSeconds,
          playbackUrl: episode?.playbackUrl,
          assetId: episode?.aegisAssetId,
          projectId: episode?.aegisProjectId,
          isAegisEmbed: episode?.isAegisEmbed,
        },
        { autoplay: false, seekTo: seconds }
      );
      if (!ok) return;
    }
    player.seekTo(seconds);
  };

  const onToggleReaction = async (type) => {
    if (!episode?.id) return;
    if (!isAuthenticated) {
      toast.info("Реакции доступны после авторизации");
      navigate("/login");
      return;
    }

    const normalizedType = normalizeType(type);
    if (!normalizedType) return;

    const prevState = reactionState;
    const nextState = buildNextReactionState(prevState, normalizedType);

    setReactionState(nextState);

    try {
      const response = await podcastsApi.toggleEpisodeReaction(episode.id, normalizedType);
      const payload = asObject(response);
      const stats = normalizeReactionState({ raw: payload });
      if (Object.keys(stats.counts).length > 0 || stats.activeType) {
        setReactionState((current) => ({
          counts: Object.keys(stats.counts).length ? stats.counts : current.counts,
          activeType: stats.activeType || current.activeType,
        }));
      }
    } catch (err) {
      setReactionState(prevState);
      toast.error(err?.message || "Не удалось отправить реакцию");
    }
  };

  const onToggleCommentLike = async (commentId) => {
    if (!commentId) return;
    if (!isAuthenticated) {
      toast.info("Реакции к комментариям доступны после авторизации");
      navigate("/login");
      return;
    }

    setComments((prev) =>
      mapComments(prev, (item) => {
        if (String(item.id) !== String(commentId)) return item;
        const likedByMe = !item.likedByMe;
        const likesCount = likedByMe ? toNumber(item.likesCount, 0) + 1 : Math.max(0, toNumber(item.likesCount, 0) - 1);
        return { ...item, likedByMe, likesCount };
      })
    );

    try {
      await podcastsApi.toggleEpisodeCommentReaction(commentId, "LIKE");
    } catch (err) {
      await loadComments();
      toast.error(err?.message || "Не удалось обновить реакцию комментария");
    }
  };

  const onSubmitComment = async () => {
    if (!episode?.id) return;
    if (!isAuthenticated) {
      toast.info("Комментарии доступны после авторизации");
      navigate("/login");
      return;
    }

    const content = safeText(commentDraft);
    if (!content) return;

    try {
      await podcastsApi.postEpisodeComment(episode.id, { content });
      setCommentDraft("");
      await loadComments();
      toast.success("Комментарий отправлен");
    } catch (err) {
      toast.error(err?.message || "Не удалось отправить комментарий");
    }
  };

  const onSubmitReply = async (parentCommentId) => {
    if (!episode?.id || !parentCommentId) return;
    if (!isAuthenticated) {
      toast.info("Комментарии доступны после авторизации");
      navigate("/login");
      return;
    }

    const content = safeText(replyDraftById[parentCommentId]);
    if (!content) return;

    try {
      await podcastsApi.postEpisodeComment(episode.id, {
        parentCommentId,
        content,
      });
      setReplyDraftById((prev) => ({ ...prev, [parentCommentId]: "" }));
      setReplyOpenById((prev) => ({ ...prev, [parentCommentId]: false }));
      await loadComments();
      toast.success("Ответ отправлен");
    } catch (err) {
      toast.error(err?.message || "Не удалось отправить ответ");
    }
  };

  const playRelatedEpisode = async (item) => {
    if (!item) return;
    navigate(`/podcasts/episodes/${encodeURIComponent(String(item.id))}`);
  };

  const playRelatedInline = async (event, item) => {
    event.stopPropagation();
    const ok = await player.playTrack(
      {
        id: item.id,
        episodeId: item.id,
        title: item.title,
        podcastTitle: item.podcastTitle,
        coverImageUrl: item.coverImageUrl,
        durationSeconds: parseTimeToSeconds(item.durationLabel),
        playbackUrl: item.playbackUrl,
        assetId: item.aegisAssetId,
        projectId: item.aegisProjectId,
        isAegisEmbed: item.isAegisEmbed,
      },
      { autoplay: true }
    );
    if (!ok) {
      navigate(`/podcasts/episodes/${encodeURIComponent(String(item.id))}`);
    }
  };

  const renderCommentThread = (item, isReply = false) => {
    const isAuthor =
      episode?.authorUserId && item?.authorUserId
        ? String(episode.authorUserId) === String(item.authorUserId)
        : false;

    return (
      <div key={`comment-${item.id}`} className={isReply ? "comment-reply-item" : "comment-thread"}>
        {!isReply ? (
          <div className="comment-item">
            <div className="comment-avatar">{initials(item.authorName)}</div>
            <div className="comment-body">
              <div className="comment-header">
                {item.authorUserId ? (
                  <button
                    type="button"
                    className="comment-name b-psych-link-btn"
                    onClick={(event) => openPsychologistProfile(event, item.authorUserId)}
                  >
                    {item.authorName}
                  </button>
                ) : (
                  <span className="comment-name">{item.authorName}</span>
                )}
                {isAuthor ? <span className="badge-author">Автор</span> : null}
                <span className="comment-time">{item.createdLabel}</span>
              </div>
              <div className="comment-text">{item.content}</div>
              <div className="comment-actions-row">
                <button
                  type="button"
                  className={`comment-action ${item.likedByMe ? "liked" : ""}`}
                  onClick={() => onToggleCommentLike(item.id)}
                >
                  ❤ <span>{toNumber(item.likesCount, 0)}</span>
                </button>
                <button
                  type="button"
                  className="comment-action"
                  onClick={() => setReplyOpenById((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                >
                  Ответить
                </button>
                <ReportContentButton
                  targetType="PODCAST_COMMENT"
                  targetId={item.id}
                  title={episode?.title || "Комментарий к подкасту"}
                  contentSnippet={item.content}
                  className="comment-action comment-action--report"
                  compact
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="comment-avatar">{initials(item.authorName)}</div>
            <div className="comment-body">
              <div className="comment-header">
                {item.authorUserId ? (
                  <button
                    type="button"
                    className="comment-name b-psych-link-btn"
                    onClick={(event) => openPsychologistProfile(event, item.authorUserId)}
                  >
                    {item.authorName}
                  </button>
                ) : (
                  <span className="comment-name">{item.authorName}</span>
                )}
                {isAuthor ? <span className="badge-author">Автор</span> : null}
                <span className="comment-time">{item.createdLabel}</span>
              </div>
              <div className="comment-text">{item.content}</div>
              <div className="comment-actions-row">
                <button
                  type="button"
                  className={`comment-action ${item.likedByMe ? "liked" : ""}`}
                  onClick={() => onToggleCommentLike(item.id)}
                >
                  ❤ <span>{toNumber(item.likesCount, 0)}</span>
                </button>
                <ReportContentButton
                  targetType="PODCAST_COMMENT"
                  targetId={item.id}
                  title={episode?.title || "Комментарий к подкасту"}
                  contentSnippet={item.content}
                  className="comment-action comment-action--report"
                  compact
                />
              </div>
            </div>
          </>
        )}

        {!isReply && replyOpenById[item.id] ? (
          <div className="comment-replies">
            <div className="comment-input-row">
              <div className="comment-avatar">{initials(me?.name || me?.login)}</div>
              <div className="comment-input-wrap">
                <textarea
                  className="comment-input"
                  placeholder="Ваш ответ..."
                  rows={2}
                  value={replyDraftById[item.id] || ""}
                  onChange={(event) =>
                    setReplyDraftById((prev) => ({
                      ...prev,
                      [item.id]: event.target.value,
                    }))
                  }
                />
                <div className="comment-input-actions">
                  <button
                    type="button"
                    className="btn-comment-cancel"
                    onClick={() => setReplyOpenById((prev) => ({ ...prev, [item.id]: false }))}
                  >
                    Отмена
                  </button>
                  <button type="button" className="btn-comment-send" onClick={() => onSubmitReply(item.id)}>
                    Ответить
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {!isReply && item.replies?.length ? (
          <div className="comment-replies">{item.replies.map((reply) => renderCommentThread(reply, true))}</div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>{`${safeText(episode?.title || "Эпизод")} | Подкасты Бюро Души`}</title>
        <meta name="description" content={safeText(episode?.description || "Эпизод подкаста психологов Бюро Души")}
        />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
      </Helmet>

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="podcasts"
          mobileOpen={sidebarOpen}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={role || "CLIENT"}
          onCloseMobile={() => setSidebarOpen(false)}
          onLogout={logout}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <form className="podcasts-topbar__search" onSubmit={onSearchSubmit}>
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="8.5" cy="8.5" r="5.5" />
                  <path d="M13 13l4 4" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="Найти подкаст, тему, психолога..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </form>
            </div>
            <div className="podcasts-topbar__right">
              {canManagePodcasts ? (
                <button className="podcasts-btn podcasts-btn--mint" type="button" onClick={() => navigate("/dashboard/podcasts")}>
                  Мои подкасты
                </button>
              ) : null}
              <button className="podcasts-btn podcasts-btn--mint" type="button" onClick={() => navigate("/podcasts")}>
                ← К каталогу
              </button>
              <div className="podcasts-topbar__separator" />
              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>
                {initials(me?.name || me?.login)}
              </button>
            </div>
          </header>

          <main className="podcasts-content">
            {loading ? <div className="podcasts-empty">Загрузка эпизода…</div> : null}
            {!loading && error ? <div className="podcasts-empty">{error}</div> : null}

            {!loading && !error && episode ? (
              <>
                <div className="podcast-detail-crumbs">
                  <Link to="/podcasts">Подкасты</Link>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 5l5 5-5 5" />
                  </svg>
                  {episode.podcastSlug || episode.podcastId ? (
                    <Link to={`/podcasts/${encodeURIComponent(String(episode.podcastSlug || episode.podcastId))}`}>
                      {episode.podcastTitle}
                    </Link>
                  ) : (
                    <span>{episode.podcastTitle}</span>
                  )}
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 5l5 5-5 5" />
                  </svg>
                  <span>{episode.title}</span>
                </div>

                <div className="podcasts-episode-layout">
                  <div>
                    <section className="ep-player-card">
                      {!usesAegisPlayer ? (
                        <div className="ep-player-top">
                          <div className="ep-cover-wrap">
                            {episode.coverImageUrl ? (
                              <img src={episode.coverImageUrl} alt={episode.title} />
                            ) : null}
                          </div>

                          <div className="ep-player-meta">
                            <div className="ep-player-badge">{episode.podcastTitle}</div>
                            <h1 className="ep-player-title">{episode.title}</h1>
                            <div className="ep-player-meta-line">
                              {episode.authorUserId ? (
                                <button
                                  type="button"
                                  className="b-psych-link-btn"
                                  onClick={(event) => openPsychologistProfile(event, episode.authorUserId)}
                                >
                                  {episode.authorName}
                                </button>
                              ) : (
                                <span>{episode.authorName}</span>
                              )}
                              <span className="dot" />
                              <span>{formatDate(episode.publishedAt)}</span>
                              <span className="dot" />
                              <span>{formatDuration(episode.durationSeconds)}</span>
                              <span className="dot" />
                              <span>{toNumber(episode.listensCount, 0).toLocaleString("ru-RU")} прослуш.</span>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="ep-player-controls">
                        {usesAegisPlayer ? (
                          <>
                            <div className="ep-aegis-player-wrap">
                              <iframe
                                className="ep-aegis-player"
                                src={episode.aegisPlayerUrl || episode.playbackUrl}
                                title={`Aegis player · ${episode.title}`}
                                loading="lazy"
                                allow="autoplay; encrypted-media; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                            <div className="ep-player-controls__aegis-note">
                              Это встроенный плеер Aegis для страницы эпизода. Для прослушивания между страницами используйте нижний плеер.
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="ep-player-controls__row">
                              <button type="button" className="ep-control-btn" onClick={() => player.seekBy(-15)}>
                                -15
                              </button>
                              <button
                                type="button"
                                className="ep-control-btn ep-control-btn--play"
                                onClick={() => {
                                  if (episodeInPlayer) {
                                    player.togglePlay();
                                    return;
                                  }
                                  playCurrentEpisode();
                                }}
                              >
                                {episodeInPlayer && player.isPlaying ? "❚❚" : "▶"}
                              </button>
                              <button type="button" className="ep-control-btn" onClick={() => player.seekBy(30)}>
                                +30
                              </button>
                              <button type="button" className="ep-control-btn ep-control-btn--speed" onClick={() => {
                                const speeds = [1, 1.25, 1.5, 2];
                                const currentIndex = speeds.indexOf(Number(player.playbackRate));
                                player.setPlaybackRate(speeds[(currentIndex + 1) % speeds.length]);
                              }}>
                                {Number(player.playbackRate).toFixed(Number(player.playbackRate) % 1 === 0 ? 0 : 2).replace(/\.00$/, "")}x
                              </button>
                            </div>

                            <input
                              className="ep-player-seek"
                              type="range"
                              min={0}
                              max={Math.max(1, Number(currentDuration) || 1)}
                              step={0.1}
                              value={Math.min(Number(episodeInPlayer ? player.currentTime : 0), Math.max(1, Number(currentDuration) || 1))}
                              onChange={onSeekEpisodeProgress}
                              style={{ "--pgp-progress": `${currentProgress}%` }}
                            />
                            <div className="ep-player-controls__times">
                              <span>{formatClock(episodeInPlayer ? player.currentTime : 0)}</span>
                              <span>{formatClock(currentDuration)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </section>

                    <section className="ep-desc-card">
                      <div className="podcasts-section-label" style={{ marginBottom: 14 }}>Описание эпизода</div>
                      <div className="ep-desc-text">{episode.description || "Описание эпизода будет опубликовано автором."}</div>

                      {episode.tags.length ? (
                        <div className="ep-tags-row">
                          {episode.tags.map((tag) => (
                            <span key={tag} className="ep-tag">#{tag.replace(/^#/, "")}</span>
                          ))}
                        </div>
                      ) : null}
                    </section>

                    <div className="reactions-row">
                      {reactionButtons.map((reaction) => (
                        <button
                          key={reaction.type}
                          type="button"
                          className={`reaction-btn ${reaction.active ? "active" : ""}`}
                          onClick={() => onToggleReaction(reaction.type)}
                        >
                          <span className="r-emoji">{reaction.emoji}</span>
                          <span className="r-count">{toNumber(reaction.count, 0)}</span>
                        </button>
                      ))}
                      <ReportContentButton
                        targetType="PODCAST_EPISODE"
                        targetId={episode.id}
                        title={episode.title}
                        contentSnippet={episode.description}
                        className="reaction-btn reaction-btn--report"
                      />
                    </div>

                    {episode.timecodes.length ? (
                      <section className="timecodes-card">
                        <div className="timecodes-header">
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#18A869" strokeWidth="2">
                            <circle cx="10" cy="10" r="8" />
                            <path d="M10 6v4l3 2" />
                          </svg>
                          <span className="ep-sidebar-card__title">Моменты выпуска</span>
                        </div>
                        <div className="timecodes-list">
                          {episode.timecodes.map((timecode) => (
                            <button
                              key={timecode.id}
                              type="button"
                              className="timecode-item"
                              onClick={async () => {
                                if (!episodeInPlayer) {
                                  const ok = await player.playTrack(
                                    {
                                      id: episode.id,
                                      episodeId: episode.id,
                                      title: episode.title,
                                      podcastTitle: episode.podcastTitle,
                                      podcastSlug: episode.podcastSlug,
                                      podcastId: episode.podcastId,
                                      coverImageUrl: episode.coverImageUrl,
                                      durationSeconds: episode.durationSeconds,
                                      playbackUrl: episode.playbackUrl,
                                      assetId: episode.aegisAssetId,
                                      projectId: episode.aegisProjectId,
                                      isAegisEmbed: episode.isAegisEmbed,
                                    },
                                    { autoplay: true, seekTo: timecode.seconds }
                                  );
                                  if (!ok) return;
                                }
                                player.seekTo(timecode.seconds);
                              }}
                            >
                              <span className="timecode-stamp">{timecode.stamp}</span>
                              <span className="timecode-label">{timecode.label}</span>
                            </button>
                          ))}
                        </div>
                      </section>
                    ) : null}

                    <section className="ep-comments-card">
                      <div className="ep-comments-head">
                        <div className="podcasts-section-label" style={{ marginBottom: 0 }}>
                          Комментарии · {commentsCount}
                        </div>
                      </div>

                      <div className="comment-input-row">
                        <div className="comment-avatar">{initials(me?.name || me?.login)}</div>
                        <div className="comment-input-wrap">
                          <textarea
                            className="comment-input"
                            placeholder={isAuthenticated ? "Напишите комментарий..." : "Авторизуйтесь, чтобы комментировать"}
                            rows={2}
                            value={commentDraft}
                            onChange={(event) => setCommentDraft(event.target.value)}
                            disabled={!isAuthenticated}
                          />
                          <div className="comment-input-actions">
                            <button type="button" className="btn-comment-send" onClick={onSubmitComment} disabled={!isAuthenticated}>
                              Отправить
                            </button>
                          </div>
                        </div>
                      </div>

                      {commentsLoading ? <div className="podcasts-empty podcasts-empty--inline">Загрузка комментариев…</div> : null}

                      {!commentsLoading && comments.length === 0 ? (
                        <div className="podcasts-empty podcasts-empty--inline">Комментариев пока нет</div>
                      ) : null}

                      {!commentsLoading && comments.length > 0 ? (
                        <div className="comment-list">{comments.map((item) => renderCommentThread(item, false))}</div>
                      ) : null}
                    </section>
                  </div>

                  <aside className="ep-sidebar">
                    {relatedEpisodes.length ? (
                      <section className="ep-sidebar-card">
                        <div className="ep-sidebar-card__head">
                          <h2 className="ep-sidebar-card__title">Другие эпизоды</h2>
                          {episode.podcastSlug || episode.podcastId ? (
                            <Link
                              className="ep-sidebar-card__link"
                              to={`/podcasts/${encodeURIComponent(String(episode.podcastSlug || episode.podcastId))}`}
                            >
                              Все →
                            </Link>
                          ) : null}
                        </div>
                        <div className="ep-sidebar-list">
                          {relatedEpisodes.map((item) => (
                            <article
                              key={`related-${item.id}`}
                              className="ep-sidebar-item"
                              role="button"
                              tabIndex={0}
                              onClick={() => playRelatedEpisode(item)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  playRelatedEpisode(item);
                                }
                              }}
                            >
                              <div className="ep-sidebar-item__cover">
                                {item.coverImageUrl ? <img src={item.coverImageUrl} alt={item.title} /> : null}
                              </div>
                              <div className="ep-sidebar-item__meta">
                                <div className="ep-sidebar-item__title">{item.title}</div>
                                <div className="ep-sidebar-item__sub">{item.durationLabel || item.publishedLabel || "Эпизод"}</div>
                              </div>
                              <button
                                type="button"
                                className="ep-play-btn"
                                onClick={(event) => playRelatedInline(event, item)}
                                title="Слушать"
                              >
                                <svg width="11" height="11" viewBox="0 0 16 16" fill="#fff">
                                  <path d="M5 3l9 5-9 5V3z" />
                                </svg>
                              </button>
                            </article>
                          ))}
                        </div>
                      </section>
                    ) : null}

                    {isAuthenticated && subscriptions.length ? (
                      <section className="ep-sidebar-card">
                        <div className="ep-sidebar-card__head">
                          <h2 className="ep-sidebar-card__title">Мои подписки</h2>
                        </div>
                        <div className="ep-sidebar-list">
                          {subscriptions.map((item) => (
                            <button
                              key={`subscription-${item.id || item.title}`}
                              type="button"
                              className="ep-sidebar-item"
                              onClick={() => {
                                if (!item.id) return;
                                navigate(`/podcasts/${encodeURIComponent(String(item.id))}`);
                              }}
                            >
                              <div className="ep-sidebar-item__cover">
                                {item.coverImageUrl ? <img src={item.coverImageUrl} alt={item.title} /> : null}
                              </div>
                              <div className="ep-sidebar-item__meta">
                                <div className="ep-sidebar-item__title">{item.title}</div>
                                <div className="ep-sidebar-item__sub">{item.badge || "Подписка"}</div>
                              </div>
                              <span className="ep-sidebar-pill" />
                            </button>
                          ))}
                        </div>
                      </section>
                    ) : null}
                  </aside>
                </div>
              </>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
